import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DataTable, { TableColumn } from "react-data-table-component";
import Select, {
  MultiValue,
  StylesConfig,
  components,
  ValueContainerProps,
} from "react-select";
import DashboardLayout from "../../components/customer/DashboardLayout";
import DashboardCard from "../../components/common/DashboardCard";
import { orderService, CustomerOrder } from "../../services/order.service";
import {
  carrierService,
  CarriersGetData,
} from "../../services/carrier.service";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBoxOpen,
  faEye,
  faCircleNotch,
  faTriangleExclamation,
  faSearch,
  faPlus,
  faTimes,
  faClipboardList,
  faMapMarkerAlt,
  faFilePdf,
} from "@fortawesome/free-solid-svg-icons";
import styles from "../../styles/pages/MyOrdersPage.module.css";
import btnStyles from "../../styles/components/Button.module.css";
import { cancelOrderService } from "../../services/cancel.order.service";
import { errorService } from "../../services/error.service";
import Swal from "sweetalert2";

const CustomValueContainer = (props: ValueContainerProps<any, true>) => {
  const { children, innerProps } = props;
  const scrollRef = React.useRef<HTMLDivElement | null>(null);

  // Merge refs to ensure react-select's internal ref also works
  const ref = (instance: HTMLDivElement | null) => {
    scrollRef.current = instance;
    if (innerProps) {
      if (typeof innerProps.ref === "function") {
        innerProps.ref(instance);
      } else if (innerProps.ref && "current" in innerProps.ref) {
        (innerProps.ref as any).current = instance;
      }
    }
  };

  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      const onWheel = (e: WheelEvent) => {
        if (e.deltaY !== 0 && el.scrollWidth > el.clientWidth) {
          e.preventDefault();
          el.scrollLeft += e.deltaY;
        }
      };
      el.addEventListener("wheel", onWheel, { passive: false });
      return () => el.removeEventListener("wheel", onWheel);
    }
  }, []);

  return (
    <components.ValueContainer
      {...props}
      innerProps={{ ...(innerProps || {}), ref }}
    >
      {children}
    </components.ValueContainer>
  );
};

const customSelectStyles: StylesConfig<{ value: number; label: string }, true> =
  {
    control: (base, state) => ({
      ...base,
      minHeight: "42px",
      maxHeight: "42px",
      borderRadius: "8px",
      borderColor: state.isFocused ? "var(--elPaket-primary-color)" : "#e2e8f0",
      boxShadow: state.isFocused ? "0 0 0 3px var(--elPaket-primary-color-light)" : "none",
      "&:hover": {
        borderColor: state.isFocused ? "var(--elPaket-primary-color)" : "#cbd5e1",
      },
      fontSize: "14px",
      background: "white",
    }),
    valueContainer: (base) => ({
      ...base,
      flexWrap: "nowrap",
      overflowX: "auto",
      maxHeight: "40px",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      "&::-webkit-scrollbar": {
        display: "none",
      },
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "var(--elPaket-primary-color-light)",
      borderRadius: "6px",
      color: "var(--elPaket-primary-color)",
      fontWeight: "600",
      minWidth: "fit-content",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "var(--brand-black)",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "var(--brand-black)",
      "&:hover": {
        backgroundColor: "var(--elPaket-primary-color-light)",
        color: "var(--elPaket-primary-color-hover)",
      },
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "12px",
      boxShadow:
        "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
      overflow: "hidden",
      border: "1px solid #e2e8f0",
      zIndex: 9999,
    }),
    option: (base, state) => ({
      ...base,
      fontSize: "14px",
      backgroundColor: state.isSelected
        ? "var(--elPaket-primary-color)"
        : state.isFocused
          ? "#f1f5f9"
          : "transparent",
      color: state.isSelected ? "white" : "#475569",
      "&:active": {
        backgroundColor: "var(--elPaket-primary-color)",
        color: "white",
      },
    }),
  };

const MyOrdersPage: React.FC = () => {
  const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Filter states
  const [searchText, setSearchText] = useState("");

  // Default dates: Last 7 days
  const today = new Date();
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 7);

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [searchParams] = useSearchParams();
  const statusParam = searchParams.get("status");
  const clearDatesParam = searchParams.get("clearDates");

  const [startDate, setStartDate] = useState(
    clearDatesParam ? "" : formatDate(sevenDaysAgo),
  );
  const [endDate, setEndDate] = useState(
    clearDatesParam ? "" : formatDate(today),
  );
  const [serviceTypeFilter, setServiceTypeFilter] = useState<string>("");

  const navigate = useNavigate();

  const fetchOrders = async (page: number, size: number) => {
    try {
      setLoading(true);
      setError(null);
      let nationalParam: boolean | undefined = undefined;
      if (serviceTypeFilter === "national") nationalParam = true;
      if (serviceTypeFilter === "international") nationalParam = false;

      const response = await orderService.getCustomerOrders({
        page,
        pageSize: size,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        national: nationalParam,
        SearchString: searchText || undefined,
      });

      if (response.statusCode === 200) {
        setOrders(response.data);
        setTotalRows(response.pagination.totalCount);
      } else {
        setError(response.message || "Error al cargar los envíos.");
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError("Ocurrió un error inesperado al cargar tus envíos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders(1, perPage);
  }, [clearDatesParam]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchOrders(page, perPage);
  };

  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    fetchOrders(page, newPerPage);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchOrders(1, perPage);
  };

  const handleRowClick = (idOrder: number) => {
    navigate(`/customer/order/${idOrder}`);
  };

  const handleCancelOrder = async (orderId: number) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: "Esta acción cancelará el envío seleccionado.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, cancelar",
      cancelButtonText: "No, volver",
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        const response = await cancelOrderService.cancelOrder({ Id: orderId });

        if (response.statusCode === 200) {
          await Swal.fire({
            icon: "success",
            title: "Cancelada",
            text: "La orden ha sido cancelada con éxito.",
          });
          // Recargar la lista actual
          fetchOrders(currentPage, perPage);
        } else {
          errorService.showErrorAlert(response, "Error");
        }
      } catch (err) {
        console.error("Error cancelling order:", err);
        errorService.showErrorAlert(err, "Error");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDownloadPdf = async (idOrder: number) => {
    try {
      const response = await orderService.getOrderSummaryPdf(idOrder);
      if (response.statusCode === 200 && response.base64Pdf) {
        const linkSource = `data:application/pdf;base64,${response.base64Pdf}`;
        const downloadLink = document.createElement("a");
        const fileName = `Invoice_${idOrder}.pdf`;

        downloadLink.href = linkSource;
        downloadLink.download = fileName;
        downloadLink.click();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.message || "No se pudo generar el PDF.",
        });
      }
    } catch (error: any) {
      console.error("Error downloading PDF:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Hubo un error al generar el PDF.",
      });
    }
  };

  const columns: TableColumn<CustomerOrder>[] = [
    {
      name: "Fecha",
      selector: (row) => row.creationDate,
      sortable: true,
      cell: (row) => {
        const date = new Date(row.creationDate);
        return isNaN(date.getTime())
          ? row.creationDate
          : date.toLocaleDateString();
      },
      width: "130px",
      hide: "sm" as any,
    },
    {
      name: "Tracking",
      selector: (row) => row.trackingNumber,
      sortable: true,
      cell: (row) => (
        <span className={styles.trackingPill}>{row.trackingNumber}</span>
      ),
      width: "160px",
    },
    {
      name: "Servicio",
      selector: (row) => row.serviceName || row.carrierName || "",
      sortable: true,
      cell: (row) => (
        <span style={{ fontWeight: 600, color: "#1e293b" }}>
          {row.serviceName || row.carrierName || "N/A"}
        </span>
      ),
      width: "180px",
    },
    {
      name: "Estado",
      selector: (row) => row.orderStatus || "",
      sortable: true,
      cell: (row) => row.orderStatus ? (
        <span className={styles.statusBadge}>{row.orderStatus}</span>
      ) : null,
      width: "140px",
      hide: "sm" as any,
    },
    {
      name: "Destinatario",
      selector: (row) => row.reciberName,
      sortable: true,
      width: "170px",
      hide: "sm" as any,
    },
    {
      name: "Destino",
      selector: (row) => row.reciberDestiny,
      sortable: true,
      cell: (row) => (
        <div className={styles.destinationInfo}>
          <span className={styles.destinationCity}>{row.reciberDestiny}</span>
          <span className={styles.destinationIso}>{row.isoDestiny}</span>
        </div>
      ),
      width: "180px",
      hide: "md" as any,
    },
    {
      name: "Peso",
      selector: (row) => row.weight,
      sortable: true,
      cell: (row) => `${row.weight} ${row.weightUnit}`,
      width: "120px",
      hide: "md" as any,
    },
    {
      name: "Estado de Pago",
      selector: (row: CustomerOrder) => {
        const isVoided = Boolean(row.isVoided || row.orderStatus?.toUpperCase() === "CANCELLED");
        const isRefunded = Boolean(row.isRefunded);
        const isPaid = Boolean(row.isPaid);

        if (isVoided && isRefunded) return "Reembolsada";
        if (isVoided && !isRefunded) return "Reembolso Pendiente";
        if (isPaid) return "Pagada";
        return "Pago Pendiente";
      },
      sortable: true,
      cell: (row: CustomerOrder) => {
        const isVoided = Boolean(row.isVoided || row.orderStatus?.toUpperCase() === "CANCELLED");
        const isRefunded = Boolean(row.isRefunded);
        const isPaid = Boolean(row.isPaid);

        let label = "Pago Pendiente";
        let badgeStyle: React.CSSProperties = {
          backgroundColor: "#fef3c7",
          color: "#92400e",
          padding: "4px 10px",
          borderRadius: "20px",
          fontSize: "12px",
          fontWeight: 600,
          display: "inline-block",
        };

        if (isVoided && isRefunded) {
          label = "Reembolsada";
          badgeStyle = { ...badgeStyle, backgroundColor: "#e0e7ff", color: "#3730a3" };
        } else if (isVoided && !isRefunded) {
          label = "Reembolso Pendiente";
          badgeStyle = { ...badgeStyle, backgroundColor: "#ffedd5", color: "#c2410c" };
        } else if (isPaid) {
          label = "Pagada";
          badgeStyle = { ...badgeStyle, backgroundColor: "#d1fae5", color: "#065f46" };
        } else {
          label = "Pago Pendiente";
          badgeStyle = { ...badgeStyle, backgroundColor: "#fef3c7", color: "#92400e" };
        }

        return <span style={badgeStyle}>{label}</span>;
      },
      width: "180px",
      hide: "md" as any,
    },
    {
      name: "Acciones",
      width: "180px",
      cell: (row) => (
        <div className="d-flex gap-2" style={{ paddingRight: "10px" }}>
          <button
            className={styles.trackButton}
            onClick={(e) => {
              e.stopPropagation();
              window.open(`/track?tracking=${row.trackingNumber}`, "_blank");
            }}
            title="Rastrear envío"
          >
            <FontAwesomeIcon icon={faMapMarkerAlt} />
          </button>

          <button
            className={styles.detailButton}
            onClick={(e) => {
              e.stopPropagation();
              window.open(`/customer/order/${row.id}`, "_blank");
            }}
            title="Ver detalle"
          >
            <FontAwesomeIcon icon={faEye} />
          </button>

          <button
            className={styles.pdfButton}
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadPdf(row.id);
            }}
            title="Descargar PDF de resumen"
          >
            <FontAwesomeIcon icon={faFilePdf} />
          </button>
        </div>
      ),
      right: true,
    },
  ];

  // ... (customStyles se mantienen igual)
  const customStyles = {
    table: {
      style: {
        backgroundColor: "white",
        borderRadius: "8px",
        overflow: "hidden",
      },
    },
    header: {
      style: {
        display: "none",
      },
    },
    headRow: {
      style: {
        backgroundColor: "var(--elPaket-primary-color)",
        color: "white",
        minHeight: "44px",
      },
    },
    headCells: {
      style: {
        color: "var(--brand-black)",
        fontWeight: "600",
        fontSize: "12px",
        textTransform: "uppercase" as any,
        letterSpacing: "0.5px",
        padding: "12px 16px",
      },
    },
    rows: {
      style: {
        minHeight: "56px",
        fontSize: "14px",
        color: "#374151",
        borderBottom: "1px solid #e5e7eb !important",
        "&:hover": {
          backgroundColor: "#f9fafb !important",
        },
      },
    },
    cells: {
      style: {
        padding: "12px 16px",
      },
    },
    pagination: {
      style: {
        borderTop: "1px solid #e5e7eb",
        marginTop: "0",
        padding: "8px 0",
      },
    },
  };

  return (
    <DashboardLayout>
      <div className={styles.myOrdersContainer}>
        <DashboardCard
          icon={faBoxOpen}
          title="Mis Envíos"
          description="Aquí puedes ver el historial y estado de todos tus paquetes enviados."
          actions={
            <button
              className={btnStyles.primary}
              onClick={() => navigate("/customer/send-package")}
            >
              <FontAwesomeIcon icon={faPlus} />
              Crear Envío
            </button>
          }
          showVerificationAlerts={true}
        />

        <div className={styles.ordersTableWrapper}>
          <form className={styles.tableActions} onSubmit={handleSearch}>
            <div className={styles.filterSection}>
              <div className={styles.filterGroup}>
                <label>Desde</label>
                <input
                  type="date"
                  className={styles.filterInput}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className={styles.filterGroup}>
                <label>Hasta</label>
                <input
                  type="date"
                  className={styles.filterInput}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <div className={styles.filterGroup}>
                <label>Tipo de Envío</label>
                <select
                  className={styles.filterSelect}
                  value={serviceTypeFilter}
                  onChange={(e) => setServiceTypeFilter(e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="national">Nacional</option>
                  <option value="international">Internacional</option>
                </select>
              </div>
            </div>

            <div className={styles.searchRow}>
              <div className={styles.searchWrapper}>
                <FontAwesomeIcon
                  icon={faSearch}
                  className={styles.searchIcon}
                />
                <input
                  type="text"
                  placeholder="Buscar por tracking o destinatario..."
                  className={styles.searchInput}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
              <button type="submit" className={btnStyles.primary}>
                <FontAwesomeIcon icon={faSearch} /> Buscar
              </button>
            </div>
          </form>

          {error ? (
            <div className={styles.ordersError}>
              <FontAwesomeIcon icon={faTriangleExclamation} size="2x" />
              <p>{error}</p>
              <button
                className={btnStyles.primary}
                onClick={() => fetchOrders(1, perPage)}
              >
                Reintentar
              </button>
            </div>
          ) : orders.length === 0 && !loading ? (
            <div className={styles.emptyContainer}>
              <FontAwesomeIcon
                icon={faClipboardList}
                size="3x"
                style={{ marginBottom: "16px", opacity: 0.5 }}
              />
              <p>No se encontraron envíos con los filtros aplicados.</p>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={orders}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationDefaultPage={currentPage}
              onChangeRowsPerPage={handlePerRowsChange}
              onChangePage={handlePageChange}
              progressPending={loading}
              progressComponent={
                <div className={styles.ordersLoading}>
                  <FontAwesomeIcon icon={faCircleNotch} spin size="2x" />
                  <p>Cargando envíos...</p>
                </div>
              }
              customStyles={customStyles}
              responsive
              persistTableHead
              noDataComponent={
                <div className={styles.ordersEmpty}>
                  <p>No se encontraron resultados</p>
                </div>
              }
              highlightOnHover
            />
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MyOrdersPage;
